import * as amplitude from "@amplitude/analytics-browser";
import { ApolloProvider } from "@apollo/client";
import "@fontsource/material-icons";
import { Box } from "@mui/material";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import { Suspense } from "react";
import { Routes } from "react-router-dom";
import { useCoachInboxAttribution } from "src/hooks/utility/useCoachInboxAttribution";
import { usePageViewTracking } from "src/hooks/utility/usePageViewTracking";
import { initializeExperimentClient } from "src/lib/experimentClient";
import client from "./clients/contentful";
import "./fonts/fonts.css"; // Licensed PP Fragment + PP Neue Montreal (see src/fonts/LICENSE.md).
import AppSkeleton from "./pages/AppSkeleton";
import { ErrorFallback } from "./pages/Common/ErrorScreen";
import { adminRoutes } from "./routes/AdminRoutes";
import { studentRoutes } from "./routes/StudentRoutes";
import SnackbarProvider from "./SnackbarProvider";

Sentry.init({
  dsn:
    process.env.NODE_ENV === "production"
      ? "https://4375f731cdb1477f8f0e533df18dfde3@o1268295.ingest.sentry.io/6657092"
      : "",
  integrations: [new BrowserTracing()],
});

if (
  process.env.REACT_APP_AMPLITUDE_API_KEY &&
  process.env.NODE_ENV === "production"
) {
  amplitude.init(process.env.REACT_APP_AMPLITUDE_API_KEY, {
    defaultTracking: true,
  });

  // Initialize Amplitude Experiment SDK for A/B testing
  initializeExperimentClient();
}

function App(): JSX.Element {
  usePageViewTracking();
  useCoachInboxAttribution();

  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <ApolloProvider client={client}>
        <SnackbarProvider>
          <Box position="static">
            <Suspense fallback={<AppSkeleton />}>
              <Routes>
                {/* Called as functions, not rendered as components. React
                    Router's <Routes> only walks fragments + <Route> children,
                    not arbitrary components — converting these to
                    <StudentRoutes /> / <AdminRoutes /> will throw at render. */}
                {studentRoutes()}
                {adminRoutes()}
              </Routes>
            </Suspense>
          </Box>
        </SnackbarProvider>
      </ApolloProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
